(ns ncr.reports.core
  (:require
   [goog.events.EventType :as EventType]
   [goog.events.KeyCodes :as KeyCodes]
   [clojure.core.match :refer [match]]
   [uix.hooks.alpha :as uix.hook]
   [uix.core.alpha :as uix]
   [cljs.core.async :as async :refer-macros [go]]
   [ncr.etc.pf :as pf]
   [ncr.amplitude :as amp]
   [ncr.widgets.core :as w]
   [ncr.widgets.edn :as w.edn]
   [ncr.widgets.attributes :as wa]
   [ncr.etc.pubsub :as ps]
   [ncr.etc.profile :as p]
   [ncr.etc.alert :as a]
   [ncr.etc.util :as u]
   [ncr.manage.preferences :as preferences]
   [ncr.reports.overview]
   [ncr.reports.revenue]
   [ncr.reports.projects]
   [ncr.reports.team]
   [ncr.widgets.dashboard :as dashboard]
   [ncr.widgets.grid :as grid]
   [ncr.me :as me]
   [ncr.reports.common :as r-common]
   [ncr.reports.core-model :as model]
   [ncr.navigate :as n]))

(defn use-prefs []
  (p/use-profile [:local "ui/preferences/report"]
    {:estimates-mode :percentages
     :show-zero-rows? true
     :mode :dashboard}))

(defn hclass [column]
  (str "ncr-report-head-" (name column)))

(def columns
  (clj->js
    (concat
      [(grid/column-accessor :heading [{:class (hclass :heading)} ""]
         (fn [cell-context]
           (match (grid/value cell-context)
             {:type :header :heading heading}
             [{:class [:pf-u-background-color-active-color-100 :pf-u-color-light-200 :pf-u-p-sm]} heading]

             {:type :data :heading heading}
             [{:class [:pf-u-pl-lg :ncr-report-data-col-heading :pf-u-p-sm]} heading]

             {:type :spacer}
             [:div {:style {:height "1em"}}]

             _
             nil)))

       (grid/column-accessor :total [{:class (hclass :total)} "Total"]
         (fn [cell-context]
           (match (grid/value cell-context)
             {:type :header}
             [{:class [:pf-u-background-color-active-color-100 :pf-u-color-light-200 :pf-u-p-sm]}]

             {:type :data :total total}
             [{:class [:pf-u-p-sm :ncr-report-data-col-total]} total]

             _
             nil)))]

      (map
        (fn [m]
          (grid/column-accessor (str "month" m) [{:class (hclass :month)} (get r-common/months m)]
            (fn [cell-context]
              (match (grid/value cell-context)
                {:type :header}
                [{:class [:pf-u-background-color-active-color-100 :pf-u-color-light-200 :pf-u-p-sm]}]

                {:type :data :months months-fn}
                (when months-fn
                  [{:class [:ncr-report-data-col-month :pf-u-p-sm]} (months-fn (inc m))])

                _
                nil))))
        (range (count r-common/months))))))

(defn- c-year-input [year set-year]
  (let
    [year-input (uix/state year)
     set-year-from-input
     (fn []
       (let [year (js/parseInt @year-input 10)]
         (when-not (js/Number.isNaN year)
           (set-year year))))

     ref (uix/ref)]

    (w/use-event ref EventType/KEYDOWN
      (fn [e]
        (when (= KeyCodes/ENTER (.-keyCode e))
          (set-year-from-input))))

    [:<>
     [w/c-toolbar-button
      {:icon :arrow-left-thick
       :on-click #(set-year (dec year))
       :tooltip "Previous year"}]

     [pf/c-toolbar-item
      [pf/c-text-input
       {:ref ref
        :on-change
        (fn [v]
          (when (re-matches #"\d*" v)
            (reset! year-input v)))
        :on-blur set-year-from-input
        :value @year-input
        :style {:width 56}}]]

     [w/c-toolbar-button
      {:icon :arrow-right-thick
       :on-click #(set-year (inc year))
       :tooltip "Next year"}]]))

(defn- c-toolbar [{:keys [progress? on-back on-reload on-edit on-collapse-all rparams on-set-year me] :as props}]
  (let [prefs (use-prefs)]
    [pf/c-toolbar (wa/filter props)
     [pf/c-toolbar-content
      [pf/c-toolbar-group {:variant :icon-button-group}
       [w/c-toolbar-button
        {:icon :arrow-left
         :on-click on-back
         :tooltip "Back"}]
       [w/c-toolbar-button
        {:icon (if progress? [pf/c-spinner {:size :md}] :reload)
         :is-disabled (or progress? (nil? on-reload))
         :on-click on-reload
         :tooltip "Reload"}]

       (when (-> me :roles (contains? :administrator))
         [w/c-toolbar-button
          {:icon :cog
           :is-disabled (nil? on-edit)
           :on-click on-edit
           :tooltip "Configure"}])]

      [pf/c-toolbar-group {:variant :icon-button-group}
       (when-some [year (:year rparams)]
         ^{:key year} [c-year-input year on-set-year])]

      (let [mode (get @prefs :mode)]
        [pf/c-toggle-group
         [pf/c-toggle-group-item
          {:is-selected (= :dashboard mode)
           :on-change #(swap! prefs assoc :mode :dashboard)
           :icon [w/c-toolbar-icon {:icon :chart-bar}] :text "Dashboard"}]
         [pf/c-toggle-group-item
          {:is-selected (= :table mode)
           :on-change #(swap! prefs assoc :mode :table)
           :icon [w/c-toolbar-icon {:icon :table}] :text "Table"}]
         [w/c-toolbar-button
          {:icon :collapse-all-outline
           :is-disabled (nil? on-collapse-all)
           :on-click on-collapse-all
           :tooltip "Collapse All"}]])

      [pf/c-toolbar-group
       {:variant :icon-button-group
        :alignment {:default :align-right}}
       [pf/c-tooltip {:content [:span "Preferences"]}
        [pf/c-toolbar-item
         [pf/c-dropdown-menu {:toggle {:icon [w/c-toolbar-icon {:icon :apps}]}}
          [pf/c-menu {:is-plain true}
           [pf/c-menu-content
            (let [estimates-mode (:estimates-mode @prefs)]
              [pf/c-menu-group {:label "Estimates"}
               [pf/c-menu-list
                [pf/c-menu-item
                 {:on-click #(swap! prefs assoc :estimates-mode :numbers)
                  :is-selected (= :numbers estimates-mode)}
                 "Show as numbers"]
                [pf/c-menu-item
                 {:on-click #(swap! prefs assoc :estimates-mode :percentages)
                  :is-selected (= :percentages estimates-mode)}
                 "Show as percentages"]
                [pf/c-menu-item
                 {:on-click #(swap! prefs assoc :estimates-mode nil)
                  :is-selected (nil? estimates-mode)}
                 "Do not show"]]])
            [pf/c-menu-group {:label "Filters"}
             [pf/c-menu-list
              [pf/c-menu-item
               {:on-click #(swap! prefs update :show-zero-rows? not)
                :is-selected (get @prefs :show-zero-rows?)}
               "Show zero rows"]]]]]]]]]]]))
(def rdef-pspec
  [:remote-cluster "report_definition"])

(def rparams-pspec
  [:remote "report/params"])

(defn- update-rdef! [rdef]
  (a/!operation
    {:operation-title "Update Report"
     :success-title "Report Updated"}
    (p/write-profile! rdef-pspec rdef)))

(def default-rdef
  {:expense [],
   :ignore [],
   :tasks [],
   :reports
   [{:id :cashflow,
     :type :cashflow,
     :revenue [],
     :costs []}
    {:id :projects-teams, :type :projects-teams}
    {:id :labels, :type :labels}],
   :people [],
   :income []})

(defn report [_]
  (let
    [me (uix.hook/context me/*context*)
     cluster-name (-> me :cluster :name)
     experimental? (preferences/use-experimental?)

     rparams (p/use-profile rparams-pspec {:year 2023})
     rprefs (use-prefs)

     rmap
     (ps/sub-reducer-map! {:gen 1}
       {:report/reload
        (fn [state]
          (update state :gen inc))

        ::edit
        (fn [state path command]
          (update-rdef!
            (model/edit-rdef path command))
          state)})

     rdef (p/use-profile rdef-pspec default-rdef)

     model
     (model/use-model @rdef @rparams @rmap @rprefs me)

     table (uix/ref nil)]

    (uix.hook/with-effect []
      (u/set-page-title "Reports" cluster-name))

    [:<>
     [pf/c-page-section
      {:class :pf-u-p-0 :aria-label "Toolbar"}
      [c-toolbar
       {:class :ncr-u-border-bottom
        :on-back
        (fn []
          (amp/navigate! {"Origin" "Reports" "Destination" "<back>"})
          (n/back!))
        :progress? (:progress? model)
        :on-reload
        (fn []
          (ps/pub! [:report/reload]))

        :on-edit
        (fn []
          (go
            (let
              [rdef
               (async/<!
                 (w.edn/!dialog
                   {:data (or @rdef default-rdef)
                    :title "Report Definition"}))]

              (when (not (w.edn/cancel? rdef))
                (update-rdef! rdef)))))

        :on-collapse-all
        #(grid/collapse-all! @table)

        :rparams @rparams
        :on-set-year
        (fn [year]
          (swap! rparams assoc :year year))

        :me me}]]

     [pf/c-page-section {:class :pf-u-p-0 :variant :light :has-overflow-scroll true :aria-label "Analyze"}
      (match model
        {:model model'}
        [w/c-error-boundary
         (if (= :table (get @rprefs :mode))
           [grid/grid
            {:table-ref table :class [:pf-c-table :pf-m-compact :pf-m-grid-md :ncr-grid :ncr-report-grid]}
            (assoc model' :columns columns)]
           [dashboard/view (assoc (:meta model') :heading (str cluster-name " " (:year @rdef)))])]

        {:progress? true}
        [w/c-empty-progress "Loading..."]

        {:error reason}
        [w/c-empty-error
         {:title "Reports Error"
          :body
          [w/c-empty-error-body reason]}])]]))
